import { EventrDate, generateID, IEventrActivity } from '@eventr/common'
import {
    drawerStore,
    MainButton,
    MobileDrawerFrame,
} from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useMemo } from 'react'
import { editItineraryTranslation } from '../../../i18n/translateHelpers'
import { bs, useBoundStore } from '../../../state/bound.store'
import {
    selectAllEditGeneralActivities,
    selectAllEditTodos,
} from '../../../state/itineraryState/currentItin.slice'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { ActivityCard } from '../../../v2/componentCards/activityCard'
import { TodoCard } from '../../../v2/componentCards/todoCard'
import { useGlobalRouter } from '../../globalRouter/useGlobalRouter'
import { BaseListPage } from '../subPages/baseListPage'

export const ActivitiesListPage = () => {
    const { pushPath } = useGlobalRouter()
    const activities = useBoundStore(selectAllEditGeneralActivities)
    const todos = useBoundStore(selectAllEditTodos)

    const allActivities = useMemo(() => {
        return [...activities, ...todos]
    }, [activities, todos])

    const orderedActivities = useMemo(() => {
        return allActivities.sort((a, b) => {
            const dateA = a.start || a.due_date
            const dateB = b.start || b.due_date
            if (dateA && dateB) {
                return (
                    new EventrDate(dateB).getUnixMs -
                    new EventrDate(dateA).getUnixMs
                )
            }
            if (dateA) {
                return -1
            }
            if (dateB) {
                return 1
            }
            return 0
        })
    }, [allActivities])

    const getInitDate = () => {
        return bs().lastUsedDateTime ?? bs().getEventInfoV2()?.data.start_date
    }
    const onGeneralActivityCreate = () => {
        const id = generateID(bs().selectedItinID, 8)
        bs().insertEditItem(8, {
            _id: id,
            type: 'general',
            start: getInitDate(),
            end: getInitDate(),
            attendees_list: [
                {
                    users: bs()
                        .selectedItineraryUsers()
                        .map((u) => ({
                            type: 'User',
                            option: u.id,
                            _id: generateID(bs().selectedItinID, 8),
                        })),
                    _id: generateID(bs().selectedItinID, 8),
                },
            ],
        } as IEventrActivity)
        pushPath(`/edit/activities/general/${id}?new=true`)
    }
    const onAddItem = () => {
        if (!bs().checkFeatureEnabled('todoList')) {
            onGeneralActivityCreate()
            return
        }
        drawerStore().openDrawer({
            node: (
                <MobileDrawerFrame
                    title={editItineraryTranslation('Select Activity Type')}
                    minHeight="30vh"
                    withCloseButton
                >
                    <Stack width={'100%'} gap={1} padding={2}>
                        <MainButton
                            icon={'Activity'}
                            text={editItineraryTranslation('General Activity')}
                            onClick={() => {
                                onGeneralActivityCreate()
                                drawerStore().closeDrawer()
                            }}
                        />
                        <MainButton
                            icon={'ToDo'}
                            text={editItineraryTranslation('To Do List')}
                            onClick={() => {
                                const id = generateID(bs().selectedItinID, 8)
                                bs().insertEditItem(8, {
                                    _id: id,
                                    type: 'todo',
                                    due_date: new EventrDate(getInitDate()).add(
                                        { days: 3 }
                                    ).dateTimeString,
                                    start_by: getInitDate(),
                                    attendees_list: [
                                        {
                                            users: bs()
                                                .selectedItineraryUsers()
                                                .map((u) => ({
                                                    type: 'User',
                                                    option: u.id,
                                                    _id: generateID(
                                                        bs().selectedItinID,
                                                        8
                                                    ),
                                                })),
                                            _id: generateID(
                                                bs().selectedItinID,
                                                8
                                            ),
                                        },
                                    ],
                                } as IEventrActivity)
                                pushPath(
                                    `/edit/activities/todos/${id}?new=true`
                                )
                                drawerStore().closeDrawer()
                            }}
                        />
                        <MainButton
                            icon={'Stand'}
                            text={editItineraryTranslation('Stand', undefined, {
                                count: 1,
                            })}
                            onClick={() => {
                                const id = generateID(bs().selectedItinID, 8)
                                bs().insertEditItem(8, {
                                    _id: id,
                                    type: 'stand',
                                } as IEventrActivity)
                                pushPath(
                                    `/edit/activities/general/${id}?new=true&type=stand`
                                )
                                drawerStore().closeDrawer()
                            }}
                        />
                    </Stack>
                </MobileDrawerFrame>
            ),
            onBackdropClick: () => drawerStore().closeDrawer(),
        })
    }
    const onDeleteAllItems = async () => {
        try {
            loadingSignal.value = true
            await bs().deleteElement(8)
        } catch (error) {
        } finally {
            loadingSignal.value = false
        }
    }
    return (
        <BaseListPage
            addItem={onAddItem}
            deleteAllitems={() => {
                onDeleteAllItems()
            }}
            itemCount={activities.length + todos.length}
            sectionName={'activities'}
        >
            {orderedActivities.map((act, index) => {
                if (act.type === 'todo') {
                    return <TodoCard id="toDoList" key={index} todo={act} />
                }

                return (
                    <ActivityCard id="activitiesList" key={index} data={act} />
                )
            })}
        </BaseListPage>
    )
}
